/**
 * 输入设备档位 —— 鼠标与手指的差异只在这一个文件里。
 * 对应技术设计 v1.4 §12.4.2「输入：一条 Pointer Events 路径」。
 *
 * 全工程**不写 mouse / touch 两套监听**：统一 Pointer Events，
 * 用 `PointerEvent.pointerType` 与 `matchMedia('(pointer: coarse)')` 区分档位。
 *
 * 为什么参数要随档位缩放：手指落点精度低于鼠标。用同一套阈值，
 * 触屏上会表现为「怎么拖都吸不上」——这是 H5 端最典型的不可用形态。
 */

export type PointerClass = "fine" | "coarse";

export interface InputProfile {
  pointerClass: PointerClass;
  /** 器材/端口最小命中半径。v1.4 §12.4.2：fine ≥24px，coarse ≥44px */
  minHitRadius: number;
  /** 吸附候选半径的缩放系数（§5.3 的基准半径 × 本系数） */
  snapScale: number;
  /** 拖拽起手阈值：小于它的位移当作点击，抑制手指按下时的微抖动 */
  dragThreshold: number;
  /** 长按毫秒数。触屏没有 hover 通道，用长按代偿「显示端口标签」 */
  longPressMs: number;
}

export const FINE_PROFILE: InputProfile = {
  pointerClass: "fine",
  minHitRadius: 24,
  snapScale: 1,
  dragThreshold: 3,
  longPressMs: 500,
};

export const COARSE_PROFILE: InputProfile = {
  pointerClass: "coarse",
  minHitRadius: 44,
  snapScale: 1.5,
  dragThreshold: 8,
  longPressMs: 350,
};

export function profileFor(pointerClass: PointerClass): InputProfile {
  return pointerClass === "coarse" ? COARSE_PROFILE : FINE_PROFILE;
}

/**
 * 探测当前设备档位。
 *
 * 优先用具体事件的 `pointerType`（同一台设备可能既有触屏又有鼠标，
 * 应该按**这一次操作**用的是什么来定，而不是按设备能力一刀切）；
 * 没有事件时回落到 media query；`matchMedia` 不存在（jsdom / SSR）时按 fine。
 */
export function detectPointerClass(event?: { pointerType?: string }): PointerClass {
  if (event?.pointerType === "touch" || event?.pointerType === "pen") return "coarse";
  if (event?.pointerType === "mouse") return "fine";

  if (typeof globalThis.matchMedia === "function") {
    try {
      return globalThis.matchMedia("(pointer: coarse)").matches ? "coarse" : "fine";
    } catch {
      return "fine";
    }
  }
  return "fine";
}

/** 命中半径按档位取下限：声明值小于档位下限时抬到下限，大于则保留。 */
export function effectiveHitRadius(declared: number, profile: InputProfile): number {
  return Math.max(declared, profile.minHitRadius);
}

/** 吸附候选半径：声明的端口热区 × 档位系数。 */
export function effectiveSnapRadius(declared: number, profile: InputProfile): number {
  return effectiveHitRadius(declared, profile) * profile.snapScale;
}
